import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LocateFixed, MapPin, Star, Trash2, Wallet, ArrowDownLeft, ArrowUpRight, Heart, ImageOff } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LocationPickerMap from '../components/LocationPickerMap';
import { SkeletonBlock } from '../components/Skeleton';
import usePageMetadata from '../hooks/usePageMetadata';
import useCurrentLocation from '../hooks/useCurrentLocation';
import { useAuth } from '../context/AuthContext';
import { useFavourites } from '../context/FavouritesContext';
import { useToast } from '../context/ToastContext';
import * as authApi from '../api/endpoints/auth';
import * as addressesApi from '../api/endpoints/addresses';
import * as walletApi from '../api/endpoints/wallet';
import * as favouritesApi from '../api/endpoints/favourites';
import { btnMd, btnPrimary, containerNarrow, focusRing, inputClass, labelClass, pageHeading, pageSection, panelHeading } from '../styles/ui';

const ProfilePage = () => {
  const { customer, setCustomer } = useAuth();
  const [profileForm, setProfileForm] = useState({ name: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);

  const [addresses, setAddresses] = useState(null);
  const [newAddress, setNewAddress] = useState({ label: '', line1: '', line2: '', city: '', state: '', pincode: '', landmark: '', coordinates: null });
  const [addingAddress, setAddingAddress] = useState(false);
  const [addressError, setAddressError] = useState(null);
  const { locating, locationError, captureLocation } = useCurrentLocation();

  const [wallet, setWallet] = useState({ status: 'loading', data: null, error: null });

  const [favourites, setFavourites] = useState({ status: 'loading', products: [], error: null });
  const { removeFavourite } = useFavourites();
  const { showToast } = useToast();

  usePageMetadata({
    title: 'My Account | Fresh & Homely',
    description: 'Manage your Fresh & Homely profile and delivery addresses.',
    canonicalPath: '/account',
  });

  useEffect(() => {
    if (customer) setProfileForm({ name: customer.name || '' });
  }, [customer]);

  const loadAddresses = () => {
    addressesApi.listAddresses().then((res) => setAddresses(res.data || []));
  };

  useEffect(loadAddresses, []);

  const loadWallet = () => {
    setWallet({ status: 'loading', data: null, error: null });
    walletApi
      .getWallet({ limit: 5 })
      .then((res) => setWallet({ status: 'success', data: res.data, error: null }))
      .catch((err) => setWallet({ status: 'error', data: null, error: err.message }));
  };

  useEffect(loadWallet, []);

  const loadFavourites = () => {
    setFavourites({ status: 'loading', products: [], error: null });
    favouritesApi
      .listFavourites()
      .then((res) => setFavourites({ status: 'success', products: res.data?.products || [], error: null }))
      .catch((err) => setFavourites({ status: 'error', products: [], error: err.message }));
  };

  useEffect(loadFavourites, []);

  const handleRemoveFavourite = async (productId) => {
    try {
      const result = await removeFavourite(productId);
      if (result?.status !== 'updated') return;
      setFavourites((prev) => ({ ...prev, products: prev.products.filter((p) => p._id !== productId) }));
      showToast('Removed from favourites', { tone: 'info' });
    } catch {
      // FavouritesContext already rolled back its own state; leave this list as-is.
    }
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const response = await authApi.patchMe(profileForm);
      setCustomer(response.data);
      setProfileMessage('Profile updated.');
      showToast('Profile updated');
    } catch (err) {
      setProfileMessage(err.message);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAddAddress = async (event) => {
    event.preventDefault();
    setAddressError(null);
    if (!newAddress.coordinates) {
      setAddressError('Please pin your location before saving this address.');
      return;
    }
    setAddingAddress(true);
    try {
      await addressesApi.createAddress(newAddress);
      setNewAddress({ label: '', line1: '', line2: '', city: '', state: '', pincode: '', landmark: '', coordinates: null });
      loadAddresses();
      showToast('Address added');
    } catch (err) {
      setAddressError(err.message);
    } finally {
      setAddingAddress(false);
    }
  };

  const handlePinLocation = async () => {
    const coordinates = await captureLocation();
    if (coordinates) setNewAddress((p) => ({ ...p, coordinates }));
  };

  const handleMapAddressChange = useCallback((geoAddress) => {
    setNewAddress((prev) => ({
      ...prev,
      line1: geoAddress.line1 || prev.line1 || '',
      line2: geoAddress.line2 || prev.line2 || '',
      city: geoAddress.city || prev.city || '',
      state: geoAddress.state || prev.state || '',
      pincode: geoAddress.pincode || prev.pincode || '',
      landmark: geoAddress.landmark || prev.landmark || '',
    }));
  }, []);

  const handleSetDefault = async (id) => {
    await addressesApi.setDefaultAddress(id);
    loadAddresses();
    showToast('Default address updated');
  };

  const handleDelete = async (id) => {
    await addressesApi.deleteAddress(id);
    loadAddresses();
    showToast('Address removed', { tone: 'info' });
  };

  if (!customer) {
    return (
      <>
        <Navbar />
        <section className="py-24 min-h-[60vh] bg-gradient-to-b from-white to-canvas">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
            <SkeletonBlock className="h-10 w-40" />
            <SkeletonBlock className="h-40 w-full rounded-3xl" />
            <SkeletonBlock className="h-48 w-full rounded-3xl" />
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <section className={pageSection}>
        <div className={`${containerNarrow} space-y-8`}>
          <h1 className={pageHeading}>My Account</h1>

          <div className="bg-gradient-primary rounded-3xl shadow-lifted p-6 text-white relative overflow-hidden">
            <div
              className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none"
              aria-hidden="true"
            />
            <div className="relative flex items-start justify-between gap-4 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                  <Wallet size={22} aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm text-white/80 font-medium">Wallet Balance</p>
                  {wallet.status === 'success' ? (
                    <p className="text-3xl font-bold leading-tight">
                      ₹{wallet.data.balance?.toFixed(2) ?? '0.00'}
                    </p>
                  ) : wallet.status === 'error' ? (
                    <p className="text-sm text-white/80">Unable to load balance</p>
                  ) : (
                    <p className="text-3xl font-bold leading-tight">₹—</p>
                  )}
                </div>
              </div>
            </div>

            {wallet.status === 'success' && (
              <div className="relative flex items-center gap-6 mb-5 text-sm">
                <div>
                  <p className="text-white/70">Credited</p>
                  <p className="font-semibold">₹{wallet.data.totalCredited?.toFixed(2) ?? '0.00'}</p>
                </div>
                <div className="w-px h-8 bg-white/20" aria-hidden="true" />
                <div>
                  <p className="text-white/70">Debited</p>
                  <p className="font-semibold">₹{wallet.data.totalDebited?.toFixed(2) ?? '0.00'}</p>
                </div>
              </div>
            )}

            {wallet.status === 'loading' && (
              <p className="relative text-sm text-white/70">Loading wallet…</p>
            )}

            {wallet.status === 'error' && (
              <button
                type="button"
                onClick={loadWallet}
                className={`relative text-sm font-medium underline underline-offset-4 hover:text-white/90 ${focusRing}`}
              >
                Try again
              </button>
            )}

            {wallet.status === 'success' && wallet.data.transactions?.length > 0 && (
              <div className="relative bg-white/10 rounded-2xl p-3 space-y-2">
                {wallet.data.transactions.map((txn) => (
                  <div key={txn.id} className="flex items-center justify-between gap-3 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      {txn.type === 'credit' ? (
                        <ArrowDownLeft size={14} className="text-emerald-200 shrink-0" aria-hidden="true" />
                      ) : (
                        <ArrowUpRight size={14} className="text-white/70 shrink-0" aria-hidden="true" />
                      )}
                      <span className="truncate">{txn.reasonLabel}</span>
                    </div>
                    <span className={`font-semibold shrink-0 ${txn.type === 'credit' ? 'text-emerald-200' : 'text-white/90'}`}>
                      {txn.type === 'credit' ? '+' : '-'}₹{txn.amount?.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {wallet.status === 'success' && wallet.data.transactions?.length === 0 && (
              <p className="relative text-sm text-white/70">No wallet activity yet.</p>
            )}
          </div>

          <div className="bg-white rounded-3xl border border-emerald-50 shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="serif text-xl text-emerald-950 flex items-center gap-2">
                <Heart size={20} className="text-accent-500" aria-hidden="true" />
                Favourites
              </h2>
              {favourites.status === 'success' && favourites.products.length > 0 && (
                <Link to="/favourites" className={`text-sm font-medium text-emerald-700 hover:text-emerald-800 ${focusRing}`}>
                  View all ({favourites.products.length})
                </Link>
              )}
            </div>

            {favourites.status === 'loading' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-emerald-100">
                    <SkeletonBlock className="w-14 h-14 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2">
                      <SkeletonBlock className="h-3.5 w-3/4" />
                      <SkeletonBlock className="h-3.5 w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {favourites.status === 'error' && (
              <div className="text-center py-6">
                <p className="text-sm text-red-600 mb-3">{favourites.error}</p>
                <button
                  type="button"
                  onClick={loadFavourites}
                  className={`text-sm font-medium text-emerald-700 underline underline-offset-4 ${focusRing}`}
                >
                  Try again
                </button>
              </div>
            )}

            {favourites.status === 'success' && favourites.products.length === 0 && (
              <p className="text-sm text-ink-secondary py-2">
                You haven't saved any favourites yet. Tap the heart icon on any product to save it here.
              </p>
            )}

            {favourites.status === 'success' && favourites.products.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {favourites.products.slice(0, 4).map((product) => (
                  <div
                    key={product._id}
                    className="floating-card flex items-center gap-3 p-3 rounded-xl border border-emerald-100 shadow-soft"
                  >
                    <Link
                      to={`/products/${product.sku || product._id}`}
                      className={`w-14 h-14 rounded-lg bg-emerald-50 overflow-hidden flex items-center justify-center shrink-0 ${focusRing}`}
                    >
                      {product.thumbnail ? (
                        <img src={product.thumbnail} alt={product.displayName} className="w-full h-full object-cover" />
                      ) : (
                        <ImageOff className="text-emerald-300" size={18} aria-hidden="true" />
                      )}
                    </Link>
                    <Link to={`/products/${product.sku || product._id}`} className={`flex-1 min-w-0 ${focusRing}`}>
                      <p className="text-sm font-semibold text-emerald-950 truncate">{product.displayName}</p>
                      <p className="text-sm text-ink-secondary">₹{product.price ?? '—'}</p>
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleRemoveFavourite(product._id)}
                      aria-label="Remove from favourites"
                      className={`text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors p-1.5 rounded-full shrink-0 ${focusRing}`}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-3xl border border-emerald-50 shadow-card p-6">
            <h2 className={`${panelHeading} mb-4`}>Profile</h2>
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className={labelClass}>Full name</label>
                <input id="name" value={profileForm.name} onChange={(e) => setProfileForm((p) => ({ ...p, name: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <p className={inputClass + ' bg-gray-50 text-ink-secondary'}>{customer.phone}</p>
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <p className={inputClass + ' bg-gray-50 text-ink-secondary'}>{customer.email}</p>
              </div>
              {profileMessage && <p className="text-sm text-emerald-700">{profileMessage}</p>}
              <button
                type="submit"
                disabled={profileSaving}
                className={`${btnPrimary} ${btnMd}`}
              >
                {profileSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>

          <div className="bg-white rounded-3xl border border-emerald-50 shadow-card p-6">
            <h2 className={`${panelHeading} mb-4`}>Delivery Addresses</h2>

            {addresses === null && (
              <div className="space-y-3 mb-6">
                {[1, 2].map((i) => (
                  <div key={i} className="p-4 rounded-xl border border-emerald-100 flex items-center gap-3">
                    <SkeletonBlock className="w-5 h-5 rounded-full shrink-0" />
                    <div className="flex-1 space-y-2">
                      <SkeletonBlock className="h-3.5 w-1/3" />
                      <SkeletonBlock className="h-3.5 w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {addresses && addresses.length > 0 && (
              <div className="space-y-3 mb-6">
                {addresses.map((address) => (
                  <div key={address._id} className="floating-card p-4 rounded-xl border border-emerald-100 shadow-soft flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="text-emerald-700 mt-0.5 shrink-0" size={18} aria-hidden="true" />
                      <div>
                        <p className="font-medium text-emerald-950">{address.label || 'Address'}{address.isDefault && ' (Default)'}</p>
                        <p className="text-sm text-ink-secondary">
                          {[address.line1, address.line2, address.city, address.state, address.pincode].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!address.isDefault && (
                        <button
                          type="button"
                          onClick={() => handleSetDefault(address._id)}
                          aria-label="Set as default"
                          className={`text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors p-1.5 rounded-full ${focusRing}`}
                        >
                          <Star size={16} aria-hidden="true" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(address._id)}
                        aria-label="Delete address"
                        className={`text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors p-1.5 rounded-full ${focusRing}`}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleAddAddress} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input placeholder="Label (e.g. Home)" className={inputClass} value={newAddress.label} onChange={(e) => setNewAddress((p) => ({ ...p, label: e.target.value }))} />
              <input placeholder="Pincode" className={inputClass} value={newAddress.pincode} onChange={(e) => setNewAddress((p) => ({ ...p, pincode: e.target.value }))} />
              <input placeholder="Address line 1" className={`sm:col-span-2 ${inputClass}`} value={newAddress.line1} onChange={(e) => setNewAddress((p) => ({ ...p, line1: e.target.value }))} />
              <input placeholder="Address line 2 (optional)" className={`sm:col-span-2 ${inputClass}`} value={newAddress.line2} onChange={(e) => setNewAddress((p) => ({ ...p, line2: e.target.value }))} />
              <input placeholder="City" className={inputClass} value={newAddress.city} onChange={(e) => setNewAddress((p) => ({ ...p, city: e.target.value }))} />
              <input placeholder="State" className={inputClass} value={newAddress.state} onChange={(e) => setNewAddress((p) => ({ ...p, state: e.target.value }))} />
              <input placeholder="Landmark (optional)" className={`sm:col-span-2 ${inputClass}`} value={newAddress.landmark} onChange={(e) => setNewAddress((p) => ({ ...p, landmark: e.target.value }))} />

              <div className="sm:col-span-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handlePinLocation}
                  disabled={locating}
                  className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border transition-colors disabled:opacity-60 ${
                    newAddress.coordinates ? 'border-emerald-700 bg-emerald-50 text-emerald-700' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                  } ${focusRing}`}
                >
                  <LocateFixed size={16} aria-hidden="true" />
                  {locating ? 'Locating...' : newAddress.coordinates ? 'Location pinned' : 'Use my current location'}
                </button>
              </div>
              {locationError && <p className="sm:col-span-2 text-sm text-red-600">{locationError}</p>}

              <div className="sm:col-span-2">
                <LocationPickerMap
                  coordinates={newAddress.coordinates}
                  onChange={(coordinates) => setNewAddress((p) => ({ ...p, coordinates }))}
                  onAddressChange={handleMapAddressChange}
                />
              </div>

              {addressError && <p className="sm:col-span-2 text-sm text-red-600">{addressError}</p>}
              <button
                type="submit"
                disabled={addingAddress}
                className={`sm:col-span-2 ${btnPrimary} ${btnMd}`}
              >
                {addingAddress ? 'Adding...' : 'Add Address'}
              </button>
            </form>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default ProfilePage;
